// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCheckbox } from '@shared/ui-kit/hooks';
import { Button, Checkbox, CircularProgress } from '@mui/material';
import { AppIcon, Heading, useDynamicDialog } from '@shared/ui-kit/components';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import {
  useCallback,
  useState,
  type PropsWithChildren,
  type FC,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

export interface ConfirmPopupProps extends PropsWithChildren {
  title: string | ReactNode;
  message: ReactNode;
  icon?: SvgIconName;
  items?: { name: string; id: string }[];
  confirmText?: string | ReactNode;
  onConfirm?: (selectedIds: string[]) => void | Promise<void>;
  cancelText?: string | ReactNode;
  onCancel?: (selectedIds: string[]) => void | Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
  selectedIds?: string[];
}

export const useConfirmPopup = () => {
  const { openDialog, closeDialog } = useDynamicDialog();

  const confirmPopup = useCallback(
    (props: ConfirmPopupProps) => {
      const ConfirmPopupWithState = () => {
        const [isLoading, setIsLoading] = useState(false);
        /* istanbul ignore next */
        const names = props.items?.map(i => i.name) ?? [];
        const { selected, toggleCheckbox } = useCheckbox(names, names);
        /* istanbul ignore next */
        const selectedIds =
          props.items?.filter(i => selected.includes(i.name)).map(i => i.id) ?? [];

        const handleConfirm = async () => {
          setIsLoading(true);
          await props.onConfirm?.(selectedIds);
          setIsLoading(false);
          closeDialog();
        };

        const handleCancel = async () => {
          setIsLoading(true);
          await props.onCancel?.(selectedIds);
          setIsLoading(false);
          closeDialog();
        };
        /* istanbul ignore next */
        const checkboxList = props.items ? (
          <div className="mt-4 max-h-40 overflow-auto text-left">
            {props.items.map(item => (
              <label key={item.id} className="flex items-center">
                <Checkbox
                  checked={selected.includes(item.name)}
                  onChange={() => toggleCheckbox(item.name)}
                  data-testid={`checkbox-${item.id}`}
                />
                <span className="ml-2 text-primary-medium">{item.name}</span>
              </label>
            ))}
          </div>
        ) : null;

        return (
          <ConfirmPopup
            {...props}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            isLoading={isLoading}
            disabled={names.length > 0 && selected.length === 0}
          >
            {checkboxList}
          </ConfirmPopup>
        );
      };

      openDialog(<ConfirmPopupWithState />, {
        showCloseButton: false,
        classes: { paper: '!w-[335px] !overflow-y-visible' },
      });
    },
    [closeDialog, openDialog],
  );

  return confirmPopup;
};

export const ConfirmPopup: FC<ConfirmPopupProps> = ({
  title,
  message,
  onConfirm,
  confirmText,
  cancelText,
  onCancel,
  icon = 'ExclamationMark',
  isLoading,
  disabled,
  selectedIds = [],
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className="text-center">
      <div className="absolute -top-10 right-[calc(50%_-_40px)] grid h-20 w-20 place-content-center rounded-full border border-primary bg-white ">
        <AppIcon icon={icon} size="2xl" color="main" />
      </div>
      <Heading variant="h2" className="!mb-5 !mt-6" title={title} />
      <p className="text-pretty px-2 text-border-dark">{message}</p>
      {children && (
        <div className="max-h-60 overflow-y-auto text-left">{children}</div>
      )}
      <div className="mt-10 grid grid-cols-2 gap-8">
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => onCancel?.(selectedIds)}
          disabled={isLoading || disabled}
        >
          {cancelText || t('common.cancel', { ns: 'common' })}
        </Button>
        <Button
          color="secondary"
          onClick={() => onConfirm?.(selectedIds)}
          disabled={isLoading || disabled}
        >
          {confirmText || t('common.ok', { ns: 'common' })}
        </Button>
      </div>
      {isLoading && (
        <div className="mt-4 flex justify-center">
          <CircularProgress size={24} />
        </div>
      )}
    </div>
  );
};
